'use client';

import type {
  InventoryResource,
  Snapshot,
  SourceAccount,
} from '@repo/api-gw-sdk';
import { AccountState, Provider } from '@repo/api-gw-sdk';

import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { NeptuneConfiguration } from './steps/neptuneConfiguration';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreNeptuneState {
  resourceId: string;
  snapshot: Snapshot;
  dbClusterIdentifier?: string;
  port?: number;
  dbClusterParameterGroup?: string;
  iamDbAuthenticationEnabled: boolean;
  enableCloudwatchLogs: string[];
  originalIamDbAuthenticationEnabled: boolean;
  originalCloudwatchLogs: string[];
  supportedCloudProviders: Provider[];
  restoreAccount?: SourceAccount;
  accounts?: SourceAccount[];
  emptyMessage?: string;
  regionName?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  initialRegionName: string;
}

const restoreNeptuneFlow: Step<RestoreNeptuneState> = {
  id: 'neptune_configuration',
  Component: NeptuneConfiguration,
};

export const RestoreNeptuneWizard = ({
  resource,
  snapshot,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { sourceAccounts } = useSourceAccounts();
  const dal = useDAL();
  const { data: restoreAccountsResponse, isLoading } =
    dal.cloudAccounts.restore.useList();

  const sourceAccount = sourceAccounts?.find(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.sourceAccountAttributes.cloudProvider === Provider.Aws &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const hasRestoreAccount = restoreAccountsResponse?.accounts?.some(
    (account) =>
      account.providerAccountId === resource.providerAccountId &&
      account.status === AccountState.ACCOUNT_STATE_CONNECTED
  );

  const allowedAccounts =
    sourceAccount && hasRestoreAccount ? [sourceAccount] : [];

  if (isLoading || !sourceAccounts || !restoreAccountsResponse) {
    return <Loader />;
  }

  return (
    <Wizard<RestoreNeptuneState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreNeptuneFlow]}
      initialState={{
        initialRegionName:
          snapshot.neptuneSnapshotProperties?.region ||
          resource.region ||
          'us-east-1',
        snapshot,
        resourceId: snapshot.resourceId,
        supportedCloudProviders: [Provider.Aws],
        restoreAccount: allowedAccounts.length > 0 ? sourceAccount : undefined,
        accounts: allowedAccounts,
        keepOriginalTags: true,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        port: 8182,
        iamDbAuthenticationEnabled:
          resource.resourceProperties?.awsNeptune?.iamDbAuthenticationEnabled ??
          false,
        enableCloudwatchLogs:
          resource.resourceProperties?.awsNeptune
            ?.enabledCloudwatchLogsExports ?? [],
        originalIamDbAuthenticationEnabled:
          resource.resourceProperties?.awsNeptune?.iamDbAuthenticationEnabled ??
          false,
        originalCloudwatchLogs:
          resource.resourceProperties?.awsNeptune
            ?.enabledCloudwatchLogsExports ?? [],
        emptyMessage: !sourceAccount
          ? 'The source account for this Neptune resource is not connected. Neptune backups are stored in AWS Backup Vault in the source account and can only be restored to the same account.'
          : !hasRestoreAccount
            ? `A restore account is required for the same AWS account (${resource.providerAccountId}). Please onboard a restore account for this AWS account.`
            : undefined,
      }}
      title='Restore Neptune'
      stepperLabels={[]}
    />
  );
};
